import { useEffect, useRef, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Clock, Flame, MapPin, ChevronDown, ExternalLink } from "lucide-react";
import { fetchRecipe } from "@/lib/api";
import { scaleIngredient } from "@/lib/scale";
import { useServings } from "@/context/ServingsContext";
import ServingsAdjuster from "@/components/ServingsAdjuster";
import HeroImage from "@/components/HeroImage";
import ProductIcon from "@/components/ProductIcon";
import { productsForRecipe } from "@/data/products";

export default function Recipe() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { servings, setServings, setBaseServings } = useServings();

  // Load recipe + honor ?serves= URL param
  useEffect(() => {
    setLoading(true);
    fetchRecipe(slug)
      .then((r) => {
        setRecipe(r);
        setBaseServings(r.base_servings);
        const urlServes = parseInt(searchParams.get("serves"), 10);
        const initial =
          Number.isFinite(urlServes) && urlServes >= 1 && urlServes <= 24
            ? urlServes
            : r.base_servings;
        setServings(initial);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [slug]);

  // Sync servings -> URL (drop param when equal to base for a clean URL)
  useEffect(() => {
    if (!recipe) return;
    const params = new URLSearchParams(searchParams);
    if (servings === recipe.base_servings) {
      params.delete("serves");
    } else {
      params.set("serves", String(servings));
    }
    setSearchParams(params, { replace: true });
  }, [servings, recipe]);

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="font-serif-alt italic text-foreground/60 text-2xl">Setting the table…</p>
      </div>
    );
  if (error || !recipe)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
        <p className="font-display text-4xl">Recipe not found</p>
        <Link to="/" className="font-serif-alt italic text-accent underline">Back to menu</Link>
      </div>
    );

  return (
    <div className="bg-background text-foreground" data-testid="recipe-page">
      <RecipeHero recipe={recipe} />
      <IngredientsSection recipe={recipe} servings={servings} />
      <StepsSection recipe={recipe} />
      <ShopTheRecipe recipe={recipe} />
      <FinalPlate recipe={recipe} />
      <ServingsAdjuster />
    </div>
  );
}

// ---------- Hero ----------
function RecipeHero({ recipe }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative h-screen overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0">
        <HeroImage src={recipe.hero_image} title={recipe.title} className="w-full h-[120%] object-cover" eyebrow={`from ${recipe.origin}`} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/40" />
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="relative z-10 h-full flex flex-col justify-end max-w-[1600px] mx-auto px-6 md:px-12 pb-24 pt-32 text-white"
      >
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="font-script text-primary text-2xl md:text-3xl mb-4" data-testid="recipe-origin-script">
            from {recipe.origin}
          </p>
          <h1
            data-testid="recipe-title"
            className="font-display text-6xl sm:text-7xl md:text-8xl lg:text-[10rem] leading-[0.85] tracking-tighter max-w-6xl"
          >
            {recipe.title}
          </h1>
          <p className="font-serif-alt italic text-xl md:text-2xl mt-6 max-w-2xl text-white/90 leading-relaxed">
            {recipe.subtitle}
          </p>
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3 mt-10 font-serif-alt text-sm md:text-base uppercase tracking-widest text-white/80">
            <span className="flex items-center gap-2"><Clock className="w-4 h-4" strokeWidth={1.5} />{recipe.total_time}</span>
            <span className="flex items-center gap-2"><Flame className="w-4 h-4" strokeWidth={1.5} />{recipe.difficulty}</span>
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4" strokeWidth={1.5} />{recipe.origin}</span>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/70 flex flex-col items-center gap-2"
      >
        <span className="font-serif-alt italic text-sm">scroll to cook</span>
        <motion.div animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ChevronDown className="w-5 h-5" strokeWidth={1.5} />
        </motion.div>
      </motion.div>
    </section>
  );
}

// ---------- Intro & Ingredients ----------
function IngredientsSection({ recipe, servings }) {
  const scaled = recipe.ingredients.map((ing) => scaleIngredient(ing, servings, recipe.base_servings));

  return (
    <section className="min-h-screen py-24 md:py-40 px-6 md:px-12" data-testid="ingredients-section">
      <div className="max-w-[1400px] mx-auto">
        {/* Intro */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mb-24"
        >
          <p className="font-script text-accent text-3xl mb-4">a note before you start</p>
          <p className="font-display text-3xl md:text-5xl leading-tight tracking-tighter">
            {recipe.intro}
          </p>
        </motion.div>

        {/* Grocery list */}
        <div className="grid md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-4 md:sticky md:top-32 md:self-start">
            <p className="font-script text-accent text-3xl mb-3">the market</p>
            <h2 className="font-display text-5xl md:text-6xl leading-[0.9] tracking-tighter mb-6">
              Grocery<br />list.
            </h2>
            <p className="font-serif-alt italic text-lg text-foreground/70 leading-relaxed">
              Quantities scale live as you change the servings dial (bottom right).
            </p>
          </div>

          <div className="md:col-span-8">
            <ul className="divide-y divide-border" data-testid="ingredients-list">
              {scaled.map((ing, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}
                  className="flex items-baseline gap-6 py-5"
                  data-testid={`ingredient-${i}`}
                >
                  <span className="font-serif-alt italic text-sm text-foreground/40 tabular-nums w-8">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <motion.span
                    key={ing.displayQty}
                    initial={{ opacity: 0.4 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    className="font-display text-2xl md:text-3xl tracking-tighter text-accent tabular-nums whitespace-nowrap min-w-[7rem]"
                    data-testid={`ingredient-qty-${i}`}
                  >
                    {ing.displayQty} {ing.unit}
                  </motion.span>
                  <span className="font-serif-alt text-lg md:text-xl text-foreground/90 flex-1 leading-snug">
                    {ing.name}
                    {ing.note && <span className="text-foreground/50 italic"> — {ing.note}</span>}
                  </span>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- Steps ----------
function StepsSection({ recipe }) {
  return (
    <section className="bg-secondary/40 py-24 md:py-40" data-testid="steps-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 mb-16 md:mb-24">
        <p className="font-script text-accent text-3xl mb-3">now, the cooking</p>
        <h2 className="font-display text-6xl md:text-8xl leading-[0.9] tracking-tighter max-w-3xl">
          One step at a time.
        </h2>
      </div>

      <div>
        {recipe.steps.map((step, i) => (
          <StepPanel key={i} step={step} index={i} total={recipe.steps.length} />
        ))}
      </div>

      {recipe.tip && (
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 mt-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="border-l-4 border-accent pl-8 py-6 bg-background/70 rounded-sm"
          >
            <p className="font-script text-accent text-2xl mb-2">chef&rsquo;s tip</p>
            <p className="font-serif-alt italic text-2xl md:text-3xl leading-snug tracking-tight">
              &ldquo;{recipe.tip}&rdquo;
            </p>
          </motion.div>
        </div>
      )}
    </section>
  );
}

function StepPanel({ step, index, total }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-150px" }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="min-h-[80vh] flex items-center py-16 md:py-24 px-6 md:px-12"
      data-testid={`step-${index}`}
    >
      <div className="max-w-[1600px] mx-auto w-full grid md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-5 relative">
          <span
            className="step-number text-[16rem] md:text-[22rem] lg:text-[26rem] text-accent/10 select-none"
            data-testid={`step-number-${index}`}
          >
            {String(index + 1).padStart(2, "0")}
          </span>
        </div>
        <div className="md:col-span-7 -mt-16 md:mt-0 relative z-10">
          <p className="font-serif-alt italic text-foreground/50 uppercase tracking-widest text-sm mb-4">
            Step {index + 1} of {total}
            {step.time_minutes && <span className="ml-4">· {step.time_minutes} min</span>}
          </p>
          <h3 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter mb-6">
            {step.title}
          </h3>
          <p className="font-serif-alt text-xl md:text-2xl leading-relaxed text-foreground/85 max-w-2xl">
            {step.body}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

// ---------- Shop the Recipe ----------
function ShopTheRecipe({ recipe }) {
  const products = productsForRecipe(recipe.slug);
  if (products.length === 0) return null;

  return (
    <section
      className="py-24 md:py-32 px-6 md:px-12 bg-background"
      data-testid="shop-the-recipe"
    >
      <div className="max-w-[1600px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="grid md:grid-cols-12 gap-8 items-end mb-14 md:mb-20"
        >
          <div className="md:col-span-8">
            <p className="font-script text-accent text-3xl mb-3">the pantry list</p>
            <h2
              className="font-display text-5xl md:text-7xl leading-[0.9] tracking-tighter"
              data-testid="shop-the-recipe-title"
            >
              Shop this recipe.
            </h2>
          </div>
          <div className="md:col-span-4">
            <p className="font-serif-alt italic text-lg text-foreground/70 border-l-2 border-accent pl-6 leading-relaxed">
              The exact bottles and boxes we&rsquo;d reach for while cooking this dish. Nothing generic — just what actually works.
            </p>
          </div>
        </motion.div>

        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
          data-testid="shop-the-recipe-grid"
        >
          {products.map((p, i) => (
            <motion.a
              key={p.slug}
              href={p.link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`recipe-product-${p.slug}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
              className="group relative flex items-stretch overflow-hidden border border-border bg-secondary/40 hover:bg-secondary/70 transition-colors duration-500"
            >
              <div className="w-28 md:w-32 shrink-0 relative flex items-center justify-center bg-secondary/60 border-r border-border overflow-hidden">
                <ProductIcon
                  name={p.icon}
                  className="w-12 h-12 md:w-14 md:h-14 text-accent/80"
                  strokeWidth={1.1}
                />
              </div>
              <div className="flex-1 p-5 md:p-6 flex flex-col justify-between gap-3">
                <div>
                  <p className="font-script text-accent text-xl leading-none mb-1">
                    {p.brand}
                  </p>
                  <p className="font-display text-xl md:text-2xl leading-tight tracking-tighter">
                    {p.name}
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-serif-alt italic text-xs uppercase tracking-widest text-foreground/50">
                    {p.origin}
                  </span>
                  <span className="inline-flex items-center gap-1.5 font-serif-alt text-sm text-accent group-hover:gap-2.5 transition-all duration-300">
                    Find it
                    <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </span>
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- Final Plate ----------
function FinalPlate({ recipe }) {
  return (
    <section className="relative h-screen overflow-hidden" data-testid="final-plate">
      <HeroImage
        src={recipe.gallery[0] || recipe.hero_image}
        title={recipe.title}
        className="absolute inset-0 w-full h-full object-cover"
        eyebrow="plated"
      />
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative z-10 h-full flex items-center justify-center text-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="font-script text-primary text-3xl md:text-4xl mb-6">plate it up</p>
          <h2 className="font-display text-white text-6xl md:text-8xl lg:text-9xl leading-[0.9] tracking-tighter italic">
            Buon appetito.
          </h2>
          <p className="font-serif-alt italic text-xl text-white/80 mt-6">
            You've earned every bite.
          </p>
          <Link
            to="/"
            data-testid="back-to-menu-link"
            className="inline-flex items-center gap-2 mt-10 px-8 py-3 border border-white/40 rounded-full font-serif-alt text-white hover:bg-white hover:text-foreground transition-colors duration-500"
          >
            Back to the menu
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
